import Immutable from 'seamless-immutable';
import { SEARCH_MODE } from '../services/TimetablesService';
import { createNestedReducer, createSearchModeBoundActions } from '../utils/ReduxUtils';
import { reducer as routeAutosuggestReducer, RouteAutosuggestTypes, INITIAL_STATE as RouteAutosuggestInitialState } from './RouteAutosuggestRedux';

/* ------------- Types and Action Creators ------------- */
// init() is a function that expects searchMode as a parameter and returns action creators as defined here
// (see createTimetablesActions jsdoc for more info)
const { Types, init: initActionCreators } = createSearchModeBoundActions({
  // Route search actions
  changeTransportMode: { params: ['mode'], isBoundToSearchMode: false },
  selectHeadsign: ['selected'],

  // Stop listing actions
  retrieveStopsRequest: ['route'],
  retrieveStopsSuccess: ['stops'],
  retrieveStopsError: ['error'],

  // timetable/schedule actions
  clearTimetable: [],
  changeDaySelector: ['daySelectorIndex'],
  selectStopRequest: ['selectedIndex', 'routeShortName', 'stopCode', 'selectedRoute'],
  selectStopSuccess: ['timetables'],
  selectStopError: ['error'],

  // Live departures integration
  gotoLiveDepartureRequest: ['selectedStop', 'history'],

  // Dismiss survey button
  dismissSurvey: ['surveyDismissed'],
});

export const TimetableTypes = Types;
export default initActionCreators;


/* ------------- Initial State ------------- */
const SEARCH_MODE_INITIAL_STATE = {
  // --- top level reducer ---
  selectedHeadsign: null,
  selectedRoute: null,
  loadingStops: false,
  loadingTimetable: false,
  stops: [],
  error: {},
  surveyDismissed: false,
  // --- subreducers ---
  routeAutosuggest: RouteAutosuggestInitialState,
};

// the state is separated by search mode, each mode contains its own instance of state properties.
// This enables to keep the search and results while switching between the modes
const INITIAL_STATE = Immutable({
  searchMode: SEARCH_MODE.BUS,
  [SEARCH_MODE.BUS]: SEARCH_MODE_INITIAL_STATE,
  [SEARCH_MODE.TRAIN]: SEARCH_MODE_INITIAL_STATE,
  [SEARCH_MODE.FERRY]: SEARCH_MODE_INITIAL_STATE,
});

/* ------------- Reducers ------------- */
const changeTransportModeHandler = (state, { mode }) => state.merge({
  searchMode: mode,
});
const selectSuggestionHandler = (state, { selected }) => state.merge({
  stops: [],
  selectedHeadsign: selected,
  timetables: null,
});
const resetSearchHandler = state => state.merge({
  selectedHeadsign: null,
  stops: [],
  timetables: null,
});

// retrieve timetable
const clearTimetableHandler = state => state.merge({
  timetables: null,
});
const selectStopRequestHandler = (state, { selectedIndex }) => state.merge({
  selectedStop: state.stops[selectedIndex],
  loadingTimetable: true,
  timetables: null,
  stops: state.stops.map((stop, index) => ({
    ...stop,
    selected: index === selectedIndex,
  })),
});
const selectStopSuccessHandler = (state, { timetables }) => state.merge({
  timetables,
  selectedDayIndex: 0,
  loadingTimetable: false,
});
const changeDaySelectorHandler = (state, { daySelectorIndex }) => state.merge({
  selectedDayIndex: daySelectorIndex,
});

const selectStopErrorHandler = (state, { error }) => state.merge({ error, loadingTimetable: false });

// retrieve stops
const retrieveStopsRequestHandler = (state, { route }) => state.merge({
  stops: [],
  selectedRoute: route,
  loadingStops: true,
  timetables: null,
});
const retrieveStopsSuccessHandler = (state, { stops }) => state.merge({ stops, loadingStops: false });

const retrieveStopsErrorHandler = (state, { error }) => state.merge({ error, loadingStops: false });

const dismissSurvey = (state, { surveyDismissed }) => state.merge({ surveyDismissed });

/* ------------- Hookup Reducers To Types ------------- */
const searchModeReducer = createNestedReducer(
  SEARCH_MODE_INITIAL_STATE,
  {
    [Types.RETRIEVE_STOPS_REQUEST]: retrieveStopsRequestHandler,
    [Types.RETRIEVE_STOPS_SUCCESS]: retrieveStopsSuccessHandler,
    [Types.RETRIEVE_STOPS_ERROR]: retrieveStopsErrorHandler,
    [Types.SELECT_STOP_REQUEST]: selectStopRequestHandler,
    [Types.SELECT_STOP_SUCCESS]: selectStopSuccessHandler,
    [Types.SELECT_STOP_ERROR]: selectStopErrorHandler,
    [Types.CHANGE_DAY_SELECTOR]: changeDaySelectorHandler,
    [Types.CLEAR_TIMETABLE]: clearTimetableHandler,
    [Types.DISMISS_SURVEY]: dismissSurvey,
    [RouteAutosuggestTypes.RESET_SEARCH]: resetSearchHandler,
    [RouteAutosuggestTypes.SELECT_SUGGESTION]: selectSuggestionHandler,
  },
  { routeAutosuggest: routeAutosuggestReducer },
);

export const reducer = createNestedReducer(
  INITIAL_STATE,
  { [Types.CHANGE_TRANSPORT_MODE]: changeTransportModeHandler },
  {
    // the state for each search mode is handled by its own subreducer. The subreducer is invoked only for actions
    // triggered in that particular search mode (e.g. if the user triggers an action in the TRAIN tab, we only change
    // the state for TRAIN - even if the user meanwhile switched to different search mode).
    [SEARCH_MODE.BUS]: (state, action) => (action.searchMode === SEARCH_MODE.BUS ? searchModeReducer(state, action) : state),
    [SEARCH_MODE.TRAIN]: (state, action) => (action.searchMode === SEARCH_MODE.TRAIN ? searchModeReducer(state, action) : state),
    [SEARCH_MODE.FERRY]: (state, action) => (action.searchMode === SEARCH_MODE.FERRY ? searchModeReducer(state, action) : state),
  },
);
